import type {
  DomainRouterConnection,
  DomainRouterJsonInput,
} from '@triedge/domain-core';
import type {
  CreateFacultyInput,
  Faculty,
  FacultyCommunication,
  FacultyDivisionAssignment,
  FacultyEmergencyContact,
  FacultyNote,
  FacultySchedule,
  FacultyTeachingAssignment,
  UpdateFacultyInput,
} from './index';

export type FacultyAssignmentItem = {
  assignmentType?: string | null;
  divisionAssignment?: FacultyDivisionAssignment | null;
  teachingAssignment?: FacultyTeachingAssignment | null;
  schedule?: FacultySchedule | null;
  emergencyContact?: FacultyEmergencyContact | null;
};

export type FacultyListResponse = DomainRouterConnection<Faculty>;
export type FacultyAssignmentListResponse = DomainRouterConnection<FacultyAssignmentItem>;
export type FacultyNoteListResponse = DomainRouterConnection<FacultyNote>;
export type FacultyCommunicationListResponse = DomainRouterConnection<FacultyCommunication>;
export type FacultyAssetUploadResponse = {
  uploadUrl: string;
  storageKey: string;
  resourceId: string;
  expiresAt: number;
};
export type FacultyDirectoryExportResponse = {
  downloadUrl: string;
  fileName: string;
  contentType?: string | null;
  expiresAt: number;
};

export const FACULTY_QUERY_OPERATION_IDS = [
  'getFaculty',
  'listFaculty',
  'listFacultyAssignments',
  'listFacultyNotes',
  'listFacultyCommunications',
] as const;

export const FACULTY_MUTATION_OPERATION_IDS = [
  'createFaculty',
  'updateFaculty',
  'addFacultyAssignment',
  'addFacultyNote',
  'sendFacultyCommunication',
  'requestFacultyAssetUpload',
  'exportFacultyDirectory',
] as const;

export type FacultyQueryOperationId = (typeof FACULTY_QUERY_OPERATION_IDS)[number];
export type FacultyMutationOperationId = (typeof FACULTY_MUTATION_OPERATION_IDS)[number];

export type FacultyQueryArgsMap = {
  getFaculty: { facultyId: string };
  listFaculty: { filter?: DomainRouterJsonInput };
  listFacultyAssignments: { facultyId: string; type?: string | null };
  listFacultyNotes: { facultyId: string };
  listFacultyCommunications: { facultyId: string };
};

export type FacultyQueryResultMap = {
  getFaculty: Faculty;
  listFaculty: FacultyListResponse;
  listFacultyAssignments: FacultyAssignmentListResponse;
  listFacultyNotes: FacultyNoteListResponse;
  listFacultyCommunications: FacultyCommunicationListResponse;
};

export type FacultyMutationArgsMap = {
  createFaculty: { input: CreateFacultyInput | DomainRouterJsonInput };
  updateFaculty: { input: UpdateFacultyInput | DomainRouterJsonInput };
  addFacultyAssignment: { input: DomainRouterJsonInput };
  addFacultyNote: { input: DomainRouterJsonInput };
  sendFacultyCommunication: { input: DomainRouterJsonInput };
  requestFacultyAssetUpload: { input: DomainRouterJsonInput };
  exportFacultyDirectory: { input: DomainRouterJsonInput };
};

export type FacultyMutationResultMap = {
  createFaculty: Faculty;
  updateFaculty: Faculty;
  addFacultyAssignment: FacultyAssignmentItem;
  addFacultyNote: FacultyNote;
  sendFacultyCommunication: FacultyCommunication;
  requestFacultyAssetUpload: FacultyAssetUploadResponse;
  exportFacultyDirectory: FacultyDirectoryExportResponse;
};
